/**
 * Authentication Logger Utility
 * Provides comprehensive logging for all auth-related events
 */

import { logger } from './logger';

// Auth Event Types
export const AUTH_EVENTS = {
  // Guard events
  GUARD_MOUNTED: 'auth:guard:mounted',
  GUARD_CHECKING: 'auth:guard:checking',
  GUARD_AUTHENTICATED: 'auth:guard:authenticated',
  GUARD_UNAUTHENTICATED: 'auth:guard:unauthenticated',
  GUARD_OFFLINE_MODE: 'auth:guard:offline',
  GUARD_TIMEOUT: 'auth:guard:timeout',
  
  // Cache events
  CACHE_HIT: 'auth:cache:hit',
  CACHE_MISS: 'auth:cache:miss',
  CACHE_EXPIRED: 'auth:cache:expired',
  CACHE_STORED: 'auth:cache:stored',
  CACHE_CLEARED: 'auth:cache:cleared',
  
  // Sign in/out events
  SIGN_IN_ATTEMPT: 'auth:signin:attempt',
  SIGN_IN_SUCCESS: 'auth:signin:success',
  SIGN_IN_FAILURE: 'auth:signin:failure',
  SIGN_OUT: 'auth:signout',
  
  // Session events
  SESSION_CHECK: 'auth:session:check',
  SESSION_VALID: 'auth:session:valid',
  SESSION_EXPIRED: 'auth:session:expired',
  SESSION_RESTORED: 'auth:session:restored',
  
  // Validation events
  VALIDATION_START: 'auth:validation:start',
  VALIDATION_SUCCESS: 'auth:validation:success',
  VALIDATION_FAILURE: 'auth:validation:failure',
} as const;

export type AuthEventType = typeof AUTH_EVENTS[keyof typeof AUTH_EVENTS];

export interface AuthLogData {
  userId?: string;
  method?: string;
  cached?: boolean;
  isNewUser?: boolean;
  error?: string;
  duration?: number;
  timestamp?: number;
  [key: string]: any;
}

// Session tracking
let sessionId: string | null = null;
let sessionStartTime: number | null = null;

/**
 * Initialize a new logging session
 */
export function initAuthSession(): string {
  sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  sessionStartTime = Date.now();
  logger.debug('AuthLogger', `Session initialized: ${sessionId}`);
  return sessionId;
}

/**
 * Get current session ID
 */
export function getAuthSessionId(): string | null {
  return sessionId;
}

/**
 * Get session duration in milliseconds
 */
export function getSessionDuration(): number {
  if (!sessionStartTime) return 0;
  return Date.now() - sessionStartTime;
}

/**
 * Log an authentication event
 */
export function logAuthEvent(
  event: AuthEventType,
  data?: AuthLogData
): void {
  const logData = {
    ...data,
    timestamp: Date.now(),
    sessionId,
    sessionDuration: getSessionDuration(),
  };
  
  const dataStr = Object.entries(logData)
    .filter(([_, v]) => v !== undefined)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(' | ');
  
  // Determine log level based on event type
  if (event.includes('failure') || event.includes('expired') || event.includes('timeout')) {
    logger.warn('Auth', `${event} | ${dataStr}`);
  } else {
    logger.debug('Auth', `${event} | ${dataStr}`);
  }
}

/**
 * Log auth guard lifecycle
 */
export function logGuardEvent(
  event: 'mounted' | 'checking' | 'authenticated' | 'unauthenticated' | 'offline' | 'timeout',
  data?: AuthLogData
): void {
  const eventMap: Record<string, AuthEventType> = {
    mounted: AUTH_EVENTS.GUARD_MOUNTED,
    checking: AUTH_EVENTS.GUARD_CHECKING,
    authenticated: AUTH_EVENTS.GUARD_AUTHENTICATED,
    unauthenticated: AUTH_EVENTS.GUARD_UNAUTHENTICATED,
    offline: AUTH_EVENTS.GUARD_OFFLINE_MODE,
    timeout: AUTH_EVENTS.GUARD_TIMEOUT,
  };
  
  logAuthEvent(eventMap[event], data);
}

/**
 * Log cache operations
 */
export function logCacheEvent(
  event: 'hit' | 'miss' | 'expired' | 'stored' | 'cleared',
  data?: AuthLogData
): void {
  const eventMap: Record<string, AuthEventType> = {
    hit: AUTH_EVENTS.CACHE_HIT,
    miss: AUTH_EVENTS.CACHE_MISS,
    expired: AUTH_EVENTS.CACHE_EXPIRED,
    stored: AUTH_EVENTS.CACHE_STORED,
    cleared: AUTH_EVENTS.CACHE_CLEARED,
  };
  
  logAuthEvent(eventMap[event], data);
}

/**
 * Log sign-in attempts and results
 */
export function logSignInEvent(
  event: 'attempt' | 'success' | 'failure',
  data?: AuthLogData
): void {
  const eventMap: Record<string, AuthEventType> = {
    attempt: AUTH_EVENTS.SIGN_IN_ATTEMPT,
    success: AUTH_EVENTS.SIGN_IN_SUCCESS,
    failure: AUTH_EVENTS.SIGN_IN_FAILURE,
  };
  
  logAuthEvent(eventMap[event], data);
}

/**
 * Log session events
 */
export function logSessionEvent(
  event: 'check' | 'valid' | 'expired' | 'restored',
  data?: AuthLogData
): void {
  const eventMap: Record<string, AuthEventType> = {
    check: AUTH_EVENTS.SESSION_CHECK,
    valid: AUTH_EVENTS.SESSION_VALID,
    expired: AUTH_EVENTS.SESSION_EXPIRED,
    restored: AUTH_EVENTS.SESSION_RESTORED,
  };
  
  logAuthEvent(eventMap[event], data);
}

// Export default logger instance
export const authLogger = {
  logAuthEvent,
  logGuardEvent,
  logCacheEvent,
  logSignInEvent,
  logSessionEvent,
  initSession: initAuthSession,
  getSessionId: getAuthSessionId,
  getSessionDuration,
};

export default authLogger;
